/**
 * Composant CRMDashboardTab - Tableau de bord CRM
 * Affiche les KPIs, l'entonnoir de conversion et les alertes
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Users, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react';

// Composant FunnelChart pour l'entonnoir de conversion
export const FunnelChart = ({ data = [] }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="space-y-2">
      {data.map((step, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="w-24 text-right text-xs text-slate-500 truncate">{step.label}</div>
          <div className="flex-1">
            <div 
              className="h-8 rounded-r flex items-center justify-end pr-2 transition-all"
              style={{ 
                width: `${(step.count / maxValue) * 100}%`,
                backgroundColor: step.color || '#8DC63F',
                minWidth: step.count > 0 ? '40px' : '0'
              }}
            >
              <span className="text-white text-sm font-medium">{step.count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Carte KPI
const DashboardCard = ({ title, value, icon: Icon, colorClass, variant = 'normal' }) => {
  const bgClass = variant === 'danger' ? 'from-red-50 to-white border-red-100' 
    : variant === 'inactive' ? 'from-slate-50 to-white border-slate-100'
    : colorClass.bg;
    
  const textClass = variant === 'danger' ? 'text-red-600' 
    : variant === 'inactive' ? 'text-slate-600'
    : colorClass.text;
    
  const valueClass = variant === 'danger' ? 'text-red-700' 
    : variant === 'inactive' ? 'text-slate-700'
    : colorClass.value;
    
  const iconClass = variant === 'danger' ? 'text-red-400' 
    : variant === 'inactive' ? 'text-slate-400'
    : colorClass.icon;

  return (
    <Card className={`bg-gradient-to-br ${bgClass}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-xs font-medium ${textClass}`}>{title}</p>
            <p className={`text-3xl font-bold ${valueClass}`}>{value}</p>
          </div>
          <Icon className={`h-10 w-10 ${iconClass}`} />
        </div>
      </CardContent>
    </Card>
  );
};

export const CRMDashboardTab = ({ 
  dashboard, 
  config, 
  onViewProspect 
}) => {
  const hasDormants = (dashboard?.dormants_count || 0) > 0;

  return (
    <div data-testid="crm-dashboard-tab">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DashboardCard
          title="Total Prospects"
          value={dashboard?.total || 0}
          icon={Users}
          colorClass={{ bg: 'from-blue-50 to-white border-blue-100', text: 'text-blue-600', value: 'text-blue-700', icon: 'text-blue-400' }}
        />
        
        <DashboardCard
          title="Taux de conversion"
          value={`${dashboard?.conversion_rate || 0}%`}
          icon={TrendingUp}
          colorClass={{ bg: 'from-green-50 to-white border-green-100', text: 'text-green-600', value: 'text-green-700', icon: 'text-green-400' }}
        />
        
        <DashboardCard
          title="Recherche financement"
          value={dashboard?.recherche_financement || 0}
          icon={DollarSign}
          colorClass={{ bg: 'from-orange-50 to-white border-orange-100', text: 'text-orange-600', value: 'text-orange-700', icon: 'text-orange-400' }}
        />
        
        <DashboardCard
          title="Dormants (>30j)"
          value={dashboard?.dormants_count || 0}
          icon={AlertTriangle}
          colorClass={{ bg: 'from-slate-50 to-white border-slate-100', text: 'text-slate-600', value: 'text-slate-700', icon: 'text-slate-400' }}
          variant={hasDormants ? 'danger' : 'inactive'}
        />
      </div>

      {/* Funnel et Alertes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#8DC63F]" />
              Entonnoir de conversion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FunnelChart data={dashboard?.funnel || []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Prospects à relancer
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.dormant_prospects?.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dashboard.dormant_prospects.map(p => (
                  <div key={p.prospect_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                    <div>
                      <p className="font-medium text-slate-800">{p.prenom} {p.nom}</p>
                      <p className="text-xs text-slate-500">
                        Dernière activité: {new Date(p.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-orange-300 text-orange-600 hover:bg-orange-100"
                      onClick={() => onViewProspect(p.email)}
                      data-testid={`view-dormant-${p.prospect_id}`}
                    >
                      Voir
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">
                Aucun prospect dormant
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats par statut */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Répartition par statut</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {config.statuts.map(s => (
              <div key={s.value} className="text-center p-3 rounded-lg bg-slate-50">
                <div 
                  className="w-4 h-4 rounded-full mx-auto mb-2"
                  style={{ backgroundColor: s.color }}
                />
                <p className="text-2xl font-bold" style={{ color: s.color }}>
                  {dashboard?.by_status?.[s.value] || 0}
                </p>
                <p className="text-xs text-slate-500 truncate">{s.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMDashboardTab;
